import React, { createContext, useContext, useRef } from 'react';
import { Form as AntdForm } from 'antd';
import { FormInstance, FormProps } from 'antd/lib/form';
import { isEmpty } from 'lodash';
import styled from 'styled-components';
import { useQueryClient } from 'react-query';

import theme from '~/constants/theme';
import { Application } from '~/types/Application';
import { getApplicationKey } from '~/constants/queryKeys';
import { getAgentReviewEdits, recordAgentReviewEdit } from '~/controllers/agentReviewEdits';
import FormItem from './FormItem';
import ShouldUpdate from './ShouldUpdate';
import Visible from './Visible';
import {
  AgentReviewEditsContext, agentEditedFieldPath, useAgentReviewEdits,
} from './AgentReviewEditsContext';

const NestedFormContext = createContext(false);

const StyledForm = styled(AntdForm)`
  .ant-form-item {
    margin-bottom: ${theme.formVerticalSpacing}px;
  }
`;

const Form = (props: FormProps | any): any => {
  const { form, onValuesChange, onFinish, ...rest } = props;
  const isNestedForm = useContext(NestedFormContext);
  const parentRecordAgentEdit = useAgentReviewEdits();
  const queryClient = useQueryClient();
  const edits = !isNestedForm
    ? getAgentReviewEdits(queryClient, queryClient.getQueryData<Application>(getApplicationKey))
    : undefined;
  const recordAgentEdit = isNestedForm ? parentRecordAgentEdit
    : edits ? (name: Parameters<typeof agentEditedFieldPath>[0]) => {
      const path = agentEditedFieldPath(name);
      if (path) recordAgentReviewEdit(edits, path);
    } : undefined;
  // Drawer edits belong to the parent form only after the drawer is submitted.
  const pendingEdits = useRef(new Map<string, Parameters<typeof agentEditedFieldPath>[0]>());
  const recordEdit = isNestedForm && recordAgentEdit
    ? (name: Parameters<typeof agentEditedFieldPath>[0]) => {
      const path = agentEditedFieldPath(name);
      if (path) pendingEdits.current.set(path, name);
    } : recordAgentEdit;

  const resetAllErrors = (formInstance: FormInstance) => {
    const fieldsWithErrors = formInstance.getFieldsError().filter((item) => !isEmpty(item.errors));
    formInstance.setFields(fieldsWithErrors.map((item) => ({ name: item.name, errors: [] })));
  };

  const handleValuesChange = (changedValues: any, values: any) => {
    if (typeof onValuesChange === 'function') {
      onValuesChange(changedValues, values);
    }
    if (form) {
      resetAllErrors(form);
    }
  };

  return (
    <NestedFormContext.Provider value>
      <AgentReviewEditsContext.Provider value={recordEdit}>
        <StyledForm
          form={form}
          validateTrigger="onBlur"
          onValuesChange={handleValuesChange}
          onFinish={(values) => {
            if (isNestedForm && recordAgentEdit) {
              pendingEdits.current.forEach(recordAgentEdit);
              pendingEdits.current.clear();
            }
            return onFinish?.(values);
          }}
          scrollToFirstError={scrollToFirstError}
          {...rest}
        />
      </AgentReviewEditsContext.Provider>
    </NestedFormContext.Provider>
  );
};

export const scrollToFirstError = { behavior: 'smooth', block: 'center' } as const;

const { List, useForm, useWatch, Provider } = AntdForm;

Form.Item = FormItem;
Form.List = List;
Form.useForm = useForm;
Form.useWatch = useWatch;
Form.Provider = Provider;
Form.ShouldUpdate = ShouldUpdate;
Form.Visible = Visible;

export default Form;
